import { useState, useEffect } from 'react';

/**
 * Fetches a member's sponsored bills and the detail record for each one.
 *
 * The list endpoint only returns ids and roles, so every row needs a second
 * request to get the title/date/pdfLink that FeedItem renders. Both
 * MemberDetailPage and SponsoredBills need the same shape, so it lives here.
 *
 * @param {string} bioGuideID
 * @param {number} limit
 * @param {string} searchQuery
 * @returns {{ bills: Array, loading: boolean, error: Error|null }}
 */
const useSponsoredBills = (bioGuideID, limit = 6, searchQuery = '') => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (!bioGuideID) {
      setBills([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    };

    const fetchBills = async () => {
      setLoading(true);

      try {
        const listUrl =
          `https://api.govglance.org/members/{bio_guid_id}` +
          `?bio_guide_id=${bioGuideID}` +
          `&limit=${limit}` +
          `&skip=0` +
          `&search=${encodeURIComponent(searchQuery)}`;

        const listResponse = await fetch(listUrl, { method: 'GET', headers });

        if (!listResponse.ok) {
          throw new Error('Failed to fetch sponsored bills');
        }

        const list = await listResponse.json();

        const details = await Promise.all(
          list.map(async (bill) => {
            const detailResponse = await fetch(
              `https://api.govglance.org/members/congresssional_bill/${bill.id}`,
              { method: 'GET', headers }
            );

            if (!detailResponse.ok) {
              throw new Error('Failed to fetch bill details');
            }

            return detailResponse.json();
          })
        );

        if (cancelled) return;

        setBills(list.map((bill, index) => ({ ...bill, details: details[index] })));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching sponsored bills:', err);
        setError(err);
        setBills([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBills();

    return () => {
      cancelled = true;
    };
  }, [bioGuideID, limit, searchQuery, apiKey]);

  return { bills, loading, error };
};

export default useSponsoredBills;